import apiClient from './client'

export interface UpdatePasswordData {
  current_password: string
  new_password: string
}

export interface UpdateEmailData {
  current_password: string
  new_email: string
}

export interface UpdateUserData {
  full_name?: string
  theme?: string
}

export interface UserProfile {
  id: string
  email: string
  full_name: string | null
  is_active: boolean
  is_superuser: boolean
  is_premium: boolean
  theme: string
  plan_type: string
  storage_type: string | null
  storage_email: string | null
  storage_user_name: string | null
  premium_start: number | null
  premium_end: number | null
  premium_auto_renew: boolean
  billing_cycle: string | null
  backed_up_time: number | null
  backed_up_success: boolean | null
}

export interface MessageResponse {
  message: string
}

// Get current user profile
export async function getUserProfile(): Promise<UserProfile> {
  const response = await apiClient.get<UserProfile>('/users/me')
  return response.data
}

// Update user profile (name, theme only - restricted endpoint)
export async function updateUserProfile(data: UpdateUserData): Promise<UserProfile> {
  const response = await apiClient.patch<UserProfile>('/users/me/profile', data)
  return response.data
}

// Update password
export async function updatePassword(data: UpdatePasswordData): Promise<MessageResponse> {
  const response = await apiClient.patch<MessageResponse>('/users/me/password', data)
  return response.data
}

// Update email
export async function updateEmail(data: UpdateEmailData): Promise<MessageResponse> {
  const response = await apiClient.patch<MessageResponse>('/users/me/email', data)
  return response.data
}

// Request password recovery
export async function requestPasswordRecovery(email: string): Promise<MessageResponse> {
  const response = await apiClient.post<MessageResponse>(`/login/password-recovery/${email}`)
  return response.data
}

// Reset password with token
export async function resetPassword(token: string, newPassword: string): Promise<MessageResponse> {
  const response = await apiClient.post<MessageResponse>('/login/reset-password/', {
    token,
    new_password: newPassword
  })
  return response.data
}

// Delete user account
export async function deleteUserAccount(): Promise<MessageResponse> {
  const response = await apiClient.delete<MessageResponse>('/users/me')
  return response.data
}

// Export notes as zip
export async function exportNotes(tagIds?: string[]): Promise<Blob> {
  const params = tagIds && tagIds.length > 0 ? { tag_ids: tagIds.join(',') } : {}
  const response = await apiClient.get('/notes/export', {
    params,
    responseType: 'blob'
  })
  return response.data
}

// Import notes from zip file
export async function importNotes(file: File, tagNames?: string[]): Promise<{ count: number; failed: string[] }> {
  const formData = new FormData()
  formData.append('file', file)
  if (tagNames && tagNames.length > 0) {
    formData.append('tags', JSON.stringify(tagNames))
  }

  const response = await apiClient.post<{ count: number; failed: string[] }>('/notes/import', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
  return response.data
}

// Storage API functions

export interface StorageStatus {
  configured: boolean
  storage_type: string | null
  storage_email: string | null
  storage_user_name: string | null
  backed_up_time: number | null
  backed_up_success: boolean | null
}

// Get storage status
export async function getStorageStatus(): Promise<StorageStatus> {
  const response = await apiClient.get<StorageStatus>('/storage/status')
  return response.data
}

// Run backup
export async function runBackup(): Promise<MessageResponse> {
  const response = await apiClient.post<MessageResponse>('/storage/backup')
  return response.data
}

